import { IFormStep } from '../../utils/formStep';
import React, { useEffect, useState } from 'react';
import { TFunction, withTranslation } from 'react-i18next';
import ProfessionalStep2ProviderProfile from './professionalStep2ProviderProfile';
import Notification, { INotificationErrorMessage } from '../../../notification/notification';
import ProfessionalStep2Reasons from './professionalStep2Reasons';
import ProfessionalStep2License from './professionalStep2License';
import ProfessionalStep2BusinessInformations from './professionalStep2BusinessInformation';
import fetchFormData, { IFormDataModel } from '../../../../helpers/fetchFormData';
import { providerModelFromFormData } from '../../../../helpers/models/providerModel';
import { ELanguage } from '../../utils/language';
import { updateProviders } from '../../../../helpers/providers';
import { updateAccount } from '../../../../helpers/account';
import {
    handleRequiredError,
    scrollToFormErrors,
    validateEmail,
    validatePastDate,
    validatePhoneExt,
    validatePhoneNumber,
    validatePositiveInt,
} from '../../utils/formErrors';
import { IRoleModel } from '../../../../helpers/models/roleModel';
import {
    IProfessionalStep2FormResultModel,
    ProfessionalStep2FormResultKeys,
} from './professionalStep2FormModel';
import { IProvinceModel } from '../../../../helpers/models/provinceModel';
import { startOverId } from '../../registration';

const ProfessionalStep2 = (props: IFormStep) => {
    const { t, account, startOver, i18n } = props;
    const [data, setData] = useState(account);
    const [provider, setProvider] = useState<number>(+(data?.role as IRoleModel)?.id || -1);

    useEffect(() => {
        if (account && account.provider.length > 0) {
            setData(account!);
        }
    }, [data, provider]);

    const handleClientFormValidation = (
        formResult: IProfessionalStep2FormResultModel,
        data: IFormDataModel[],
        t: TFunction
    ) => {
        let errorList: INotificationErrorMessage[] = [];

        // Required fields
        errorList = handleRequiredError(formResult, data, t).concat(errorList);

        // Custom validation
        if (formResult.form_professional_step_2_field_phone_ext) {
            const extValidation = validatePhoneExt(
                ProfessionalStep2FormResultKeys.PHONE_EXT,
                formResult.form_professional_step_2_field_phone_ext,
                t
            );
            if (extValidation) {
                errorList.push({
                    message: extValidation,
                    focusElement: ProfessionalStep2FormResultKeys.PHONE_EXT,
                    weight: 3,
                });
            }
        }

        if (formResult.form_professional_step_2_field_phone) {
            const phoneValidation = validatePhoneNumber(
                ProfessionalStep2FormResultKeys.PHONE,
                formResult.form_professional_step_2_field_phone,
                t
            );
            if (phoneValidation) {
                errorList.push({
                    message: phoneValidation,
                    focusElement: ProfessionalStep2FormResultKeys.PHONE,
                    weight: 2,
                });
            }
        }
        if (formResult.form_professional_step_2_field_email) {
            const emailValidation = validateEmail(
                ProfessionalStep2FormResultKeys.EMAIL,
                formResult.form_professional_step_2_field_email,
                t
            );
            if (emailValidation) {
                errorList.push({
                    message: emailValidation,
                    focusElement: ProfessionalStep2FormResultKeys.NUMBER,
                    weight: 4,
                });
            }
        }

        if (formResult.form_professional_step_2_field_number) {
            const numberValidation = validatePositiveInt(
                ProfessionalStep2FormResultKeys.NUMBER,
                +formResult.form_professional_step_2_field_number,
                t
            );
            if (numberValidation) {
                errorList.push({
                    messagePrefix: 'form_generic_positive_int_error_prefix',
                    message: 'form_professional_step_2_number',
                    messageSuffix: 'form_generic_positive_int_error_suffix',
                    lowerCaseMessage: true,
                    focusElement: ProfessionalStep2FormResultKeys.NUMBER,
                    weight: 10,
                });
            }
        }
        if (formResult.form_professional_step_2_field_issue_date) {
            const dateValidation = validatePastDate(
                ProfessionalStep2FormResultKeys.ISSUE_DATE,
                formResult.form_professional_step_2_field_issue_date,
                t
            );
            if (dateValidation) {
                errorList.push({
                    message: dateValidation,
                    focusElement: ProfessionalStep2FormResultKeys.ISSUE_DATE,
                    weight: 9,
                });
            }
        }
        if (formResult.form_professional_step_2_field_enterprise_number) {
            const enterpriseNumberValidation = validatePositiveInt(
                ProfessionalStep2FormResultKeys.ENTERPRISE_NUMBER,
                +formResult.form_professional_step_2_field_enterprise_number,
                t
            );
            if (enterpriseNumberValidation) {
                errorList.push({
                    messagePrefix: 'form_generic_positive_int_error_prefix',
                    message: 'form_professional_step_2_enterprise_number',
                    messageSuffix: 'form_generic_positive_int_error_suffix',
                    lowerCaseMessage: true,
                    focusElement: ProfessionalStep2FormResultKeys.ENTERPRISE_NUMBER,
                    weight: 10,
                });
            }
        }
        if (formResult.form_professional_step_2_field_enterprise_tps) {
            const tpsValidation = validatePositiveInt(
                ProfessionalStep2FormResultKeys.TPS,
                +formResult.form_professional_step_2_field_enterprise_tps,
                t
            );
            if (tpsValidation) {
                errorList.push({
                    messagePrefix: 'form_generic_positive_int_error_prefix',
                    message: 'form_professional_step_2_enterprise_tps',
                    messageSuffix: 'form_generic_positive_int_error_suffix',
                    lowerCaseMessage: true,
                    focusElement: ProfessionalStep2FormResultKeys.TPS,
                    weight: 11,
                });
            }
        }

        if (formResult.form_professional_step_2_field_enterprise_rt) {
            const rtValidation = validatePositiveInt(
                ProfessionalStep2FormResultKeys.RT,
                +formResult.form_professional_step_2_field_enterprise_rt,
                t
            );
            if (rtValidation) {
                errorList.push({
                    messagePrefix: 'form_generic_positive_int_error_prefix',
                    message: 'form_professional_step_2_enterprise_rt',
                    messageSuffix: 'form_generic_positive_int_error_suffix',
                    lowerCaseMessage: true,
                    focusElement: ProfessionalStep2FormResultKeys.RT,
                    weight: 12,
                });
            }
        }

        return errorList;
    };

    const handleSubmitProfessionalStep2 = (e: any) => {
        e.preventDefault();
        if (e.nativeEvent.submitter.id === 'eligibility-submit-button') {
            return;
        }
        const data = fetchFormData(e);
        const formResult = Object.assign(
            {},
            ...data.map((x) => ({ [x.name]: x.value }))
        ) as IProfessionalStep2FormResultModel;

        const errorList = handleClientFormValidation(formResult, data, t);

        if (!errorList.length) {
            const provider = providerModelFromFormData(formResult);
            provider.lng =
                data.find(
                    (item) => item.name === ProfessionalStep2FormResultKeys.LNG && item.checked
                )?.value === ELanguage.English.toString()
                    ? 'en'
                    : 'fr';
            // set Providers
            updateProviders(i18n.language, account!.id, [provider]);

            // update step in account
            const newFormData = Object.assign({}, account);
            if (newFormData.step >= 1) {
                newFormData.step++;
            }

            // temporary since set in BE
            newFormData.dateLastAction = new Date().toISOString().split('T')[0];
            // set number to province instead of the returned object
            newFormData.province = +(account!.province as IProvinceModel).id;
            newFormData.role = +formResult.form_professional_step_2_field_profession;

            // set reasons retrieve to account
            newFormData.reason = formResult.form_professional_step_2_field_register_reason;
            newFormData.reasonOther = formResult.form_professional_step_2_field_reason_other;

            updateAccount(i18n.language, account!.id, { ...newFormData });
        }

        if (errorList.length) {
            props.setError(errorList);
            scrollToFormErrors();
        }
    };

    return (
        <div className="registration-professional_step_container">
            <Notification
                error={false}
                label={t('popover_aria_label', { ns: 'accessibility' })}
                messages={[{ message: 'form_organization_step_2_notice', weight: 0 }]}
            />
            <form className="registration-container" onSubmit={handleSubmitProfessionalStep2}>
                <ProfessionalStep2ProviderProfile
                    account={account}
                    setProviderProps={setProvider}
                />
                <ProfessionalStep2Reasons account={account} />
                <hr className="registration-professional-section-divider" />
                <ProfessionalStep2License account={account} provider={provider} />
                <ProfessionalStep2BusinessInformations account={account} />
                <>
                    <div className="header-3 header-3--purple registration_form_header">
                        {t('form_professional_step_2_comments_title')}
                    </div>
                    <div className="field-container registration-field">
                        <label
                            htmlFor="form_professional_step_2_comment"
                            className="field-label field-label-container"
                            aria-hidden
                            tabIndex={-1}
                        >
                            {t('form_professional_step_2_comment').concat(' ')}
                            <span className="field-label--optional">
                                {t('form_professional_step_2_comment_optional')}
                            </span>
                        </label>
                        <textarea
                            name={ProfessionalStep2FormResultKeys.COMMENT}
                            id="form_professional_step_2_comment"
                            className="field-input field-value"
                            defaultValue={account!.provider[0]?.comment}
                            aria-label={t('form_professional_step_2_comment')
                                .addSpace()
                                .concat(t('form_professional_step_2_comment_optional'))}
                        />
                    </div>
                </>
                <div className="registration-professional-button-container">
                    <button
                        className="button button--secondary registration-professional-button"
                        onClick={startOver}
                        id={startOverId}
                    >
                        {t('form_professional_step_2_button_restart')}
                    </button>
                    <div className="registration-professional-standalone-button-container">
                        <a
                            tabIndex={0}
                            data-step={1}
                            className="standalone-link registration-button-standalone-link registration-professional-standalone-link"
                            onClick={props.goBack}
                            role="button"
                        >
                            {t('form_professional_step_2_button_previous')}
                        </a>
                        <button
                            className="button button--primary registration-professional-button"
                            type="submit"
                        >
                            {t('form_professional_step_2_button_next')}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default withTranslation()(ProfessionalStep2);
